import type { VectorNode } from "@src/features/equation-editor/equation";
import type { Dimension } from "@src/math/types";

export const REORDER_DRAG_MIME_TYPE = "application/x-mattriz-reorder";

export function initializeReorderDrag(dataTransfer: DataTransfer): void {
  dataTransfer.effectAllowed = "move";
  dataTransfer.setData(REORDER_DRAG_MIME_TYPE, "reorder");
}

export function isInteractiveDragTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("input, button, select, textarea, a, summary") !== null
  );
}

export function createVectorDragPreview<D extends Dimension>(
  vector: VectorNode<D>,
  dimension: D,
): HTMLElement {
  const preview = document.createElement("div");
  preview.className = "vector-column-drag-preview";
  preview.dataset.dragPreview = "true";
  preview.setAttribute("aria-hidden", "true");
  preview.style.setProperty("--vector", vector.color);
  Object.assign(preview.style, {
    position: "fixed",
    top: "-10000px",
    left: "-10000px",
    pointerEvents: "none",
  });

  const header = document.createElement("div");
  header.className = "vector-column-label";
  const label = document.createElementNS(
    "http://www.w3.org/1998/Math/MathML",
    "math",
  );
  label.className = "vector-label";
  label.append(createVectorSymbolElement(vector.label));
  const color = document.createElement("span");
  color.className = "vector-color-label";
  header.append(label, color);

  const values = document.createElement("div");
  values.className = "vector-drag-preview-values";
  vector.coordinates.slice(0, dimension).forEach((coordinate) => {
    const output = document.createElement("output");
    output.className = "vector-drag-preview-cell";
    output.textContent = coordinate;
    values.append(output);
  });
  preview.append(header, values);
  return preview;
}

interface DragPreviewBounds {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export function calculateDragPreviewAnchor(
  pointer: { readonly clientX: number; readonly clientY: number },
  sourceBounds: DragPreviewBounds,
  previewBounds: DragPreviewBounds,
  previewContentBounds: DragPreviewBounds,
): { readonly x: number; readonly y: number } {
  return {
    x:
      pointer.clientX -
      (sourceBounds.left + sourceBounds.width / 2) +
      (previewContentBounds.left -
        previewBounds.left +
        previewContentBounds.width / 2),
    y:
      pointer.clientY -
      (sourceBounds.top + sourceBounds.height / 2) +
      (previewContentBounds.top -
        previewBounds.top +
        previewContentBounds.height / 2),
  };
}

function createVectorSymbolElement(label: string): MathMLElement {
  const namespace = "http://www.w3.org/1998/Math/MathML";
  const match = /^([a-zA-Z]+)(\d+)$/.exec(label);
  if (!match) {
    const identifier = document.createElementNS(namespace, "mi");
    identifier.textContent = label;
    return identifier;
  }
  const identifierText = match[1];
  const numberText = match[2];
  const subscript = document.createElementNS(namespace, "msub");
  const identifier = document.createElementNS(namespace, "mi");
  identifier.textContent = identifierText;
  const number = document.createElementNS(namespace, "mn");
  number.textContent = numberText;
  subscript.append(identifier, number);
  return subscript;
}
